'use client'

import { Suspense, useCallback, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'next/navigation'
import { useRouter } from '@/i18n/routing'
import { SignInPrompt } from '@/components/auth/sign-in-prompt'
import { LoginDialog } from '@/components/auth/login-dialog'
import { CollectionProgressBar } from '@/components/collections/collection-progress'
import { CreateCollectionDialog } from '@/components/collections/create-collection-dialog'
import { CollectionItemsTable } from '@/components/collections/collection-items-table'
import { DeckImportExportPanel } from '@/components/collections/deck-import-export-panel'
import {
  AddStudyNoteButton,
  StudyNoteDialog
} from '@/components/collections/study-note-dialog'
import { FlashcardSession } from '@/components/fsrs/flashcard-session'
import { PageLayout } from '@/components/layout/page-layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AnimatedCounter } from '@/components/ui/animated-counter'
import { BodyText, MinorHeading } from '@/components/ui/typography'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/hooks/useAuth'
import { Link } from '@/i18n/routing'
import { useOffline } from '@/components/offline-context'
import {
  getAllCollections,
  getOrCreateBookmarkCollection,
  curatedCollectionId,
  getAllCuratedCollectionsProgress,
  getUserCollectionsProgress,
  getCollectionById,
  getCollectionType,
  getCuratedSlug,
  getUserCollectionSettings,
  removeBookmark,
  upsertUserCollectionSettings,
  type CollectionProgress
} from '@/lib/collections'
import {
  deleteStudyDeck,
  duplicateStudyDeck,
  getStudyDeckItems,
  removeCardFromDeck,
  updateStudyDeck
} from '@/lib/study'
import { invalidateCardQueries } from '@/lib/fsrs/invalidate-queries'
import { useDashboardStats } from '@/hooks/useDashboardStats'
import { useStudyInsights } from '@/hooks/useStudyInsights'
import { getAllCuratedCollections } from '@/content/curated-collections'
import {
  IconArrowLeft,
  IconArrowRight,
  IconBookmark,
  IconCards,
  IconCopy,
  IconDots,
  IconEdit,
  IconFlame,
  IconFolderPlus,
  IconList,
  IconPlayerPlay,
  IconRefresh,
  IconSettings,
  IconTable,
  IconHelpCircle,
  IconTrash,
  IconX
} from '@tabler/icons-react'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useLocale, useTranslations } from 'next-intl'
import type { CollectionDisplay, CollectionId } from '@/types/collections'
import type { StudyDeckItem } from '@/types/study'
import { toast } from 'sonner'

function AppCollectionsContent() {
  const router = useRouter()
  const searchParams = useSearchParams()

  // Check for collection ID in query params
  const collectionId = searchParams.get('id') as CollectionId | null

  // If we have a collection ID, show the detail view
  if (collectionId) {
    return (
      <CollectionDetailView
        collectionId={collectionId}
        onBack={() => router.push('/collections')}
      />
    )
  }

  // Otherwise show the collections list
  return <CollectionsListView />
}

function DeckActions({
  canDelete,
  duplicatePending,
  deletePending,
  onEdit,
  onDuplicate,
  onDelete,
  mode = 'buttons'
}: {
  canDelete: boolean
  duplicatePending: boolean
  deletePending: boolean
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  mode?: 'buttons' | 'menu'
}) {
  const actions = [
    {
      label: 'Edit deck',
      icon: IconEdit,
      onClick: onEdit
    },
    {
      label: 'Duplicate deck',
      icon: IconCopy,
      onClick: onDuplicate,
      disabled: duplicatePending
    },
    ...(canDelete
      ? [
          {
            label: 'Delete deck',
            icon: IconTrash,
            onClick: onDelete,
            disabled: deletePending,
            destructive: true
          }
        ]
      : [])
  ]

  if (mode === 'menu') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="Deck actions">
            <IconDots className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {actions.map((action) => (
            <DropdownMenuItem
              key={action.label}
              variant={action.destructive ? 'destructive' : undefined}
              disabled={action.disabled}
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4" />
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {actions.map((action) => (
        <Button
          key={action.label}
          variant="outline"
          disabled={action.disabled}
          onClick={action.onClick}
          className={
            action.destructive
              ? 'text-destructive hover:text-destructive'
              : undefined
          }
        >
          <action.icon className="mr-2 h-4 w-4" />
          {action.label}
        </Button>
      ))}
    </div>
  )
}

function CollectionsListView() {
  const t = useTranslations('collections')
  const tCommon = useTranslations('common')
  const locale = useLocale()
  const { user, isLoading: authLoading } = useAuth()
  const { data: stats, isLoading: statsLoading } = useDashboardStats()
  const { data: insights } = useStudyInsights()

  // Always get curated collections (static, no userId needed)
  const curatedLists = useMemo(() => getAllCuratedCollections(locale), [locale])
  const curatedCollections: (CollectionDisplay & {
    coverIcon?: string | null
  })[] = useMemo(() => {
    return curatedLists.map((c) => ({
      type: 'curated' as const,
      id: curatedCollectionId(c.slug),
      slug: c.slug,
      name: c.title,
      description: c.description,
      item_count: c.item_count,
      coverIcon: c.coverIcon
    }))
  }, [curatedLists])

  // Group curated collections by coverIcon for accordion display
  const groupedCurated = useMemo(() => {
    const groups: Record<string, typeof curatedCollections> = {}
    curatedCollections.forEach((c) => {
      const key = c.coverIcon?.toUpperCase() || 'Other'
      if (!groups[key]) groups[key] = []
      groups[key].push(c)
    })
    return groups
  }, [curatedCollections])

  // Get user collections if logged in
  const {
    data: userCollectionsData = [],
    isLoading: collectionsLoading,
    isFetching: collectionsFetching
  } = useQuery({
    queryKey: ['all-collections', user?.id],
    queryFn: () => {
      if (!user?.id) return []
      return getAllCollections(user.id)
    },
    enabled: !authLoading,
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  })

  // Get or ensure bookmark collection exists for logged-in users
  const { data: bookmarkCollection } = useQuery({
    queryKey: ['bookmark-collection', user?.id],
    queryFn: async () => {
      if (!user?.id) return null
      return getOrCreateBookmarkCollection(user.id)
    },
    enabled: !authLoading && !!user?.id,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false
  })

  // Fetch progress for curated collections
  const { data: curatedProgressMap } = useQuery({
    queryKey: ['curated-collections-progress', user?.id],
    queryFn: async () => {
      if (!user?.id) return new Map<string, CollectionProgress>()
      return getAllCuratedCollectionsProgress(user.id)
    },
    enabled: !authLoading && !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  })

  // Fetch progress for user collections (includes Anki-style counts)
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC'
  const { data: userProgressMap } = useQuery({
    queryKey: ['user-collections-progress', user?.id, timeZone],
    queryFn: async () => {
      if (!user?.id) return new Map<string, CollectionProgress>()
      return getUserCollectionsProgress(user.id, timeZone)
    },
    enabled: !authLoading && !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  })

  // Separate user collections by type
  const userCollections = userCollectionsData.filter((c) => c.type === 'user')
  const existingBookmarkCollection = userCollections.find((c) => c.is_bookmark)
  // Use existing bookmark from collections query (which has item_count) or use the fetched one
  const bookmarkToShow =
    existingBookmarkCollection ||
    (bookmarkCollection
      ? {
          type: 'user' as const,
          id: bookmarkCollection.id,
          name: bookmarkCollection.name,
          description: bookmarkCollection.description || null,
          item_count: 0, // Will show 0 until collections query includes it
          is_bookmark: true,
          created_at: bookmarkCollection.created_at,
          updated_at: bookmarkCollection.updated_at
        }
      : null)

  const otherUserCollections = userCollections.filter((c) => !c.is_bookmark)
  const virtualCollections = userCollectionsData.filter(
    (c) => c.type === 'virtual'
  )

  const isLoading =
    authLoading ||
    (collectionsLoading && userCollectionsData.length === 0 && user)
  const showAuthPrompt = !authLoading && !user

  return (
    <PageLayout
      variant="workspace"
      title="Decks"
      description="Manage cards and spaced repetition."
    >
      <div className="mx-auto box-border flex w-full max-w-6xl flex-1 flex-col gap-5 px-4 py-5 pb-16 md:px-6 md:py-6">
        {/* Stats Bar - Compact inline stats */}
        {user && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <div className="bg-muted/45 inline-flex h-8 items-center gap-2 rounded-full px-3">
              <IconFlame
                className={`h-4 w-4 ${stats?.studiedToday ? 'text-orange-500' : 'text-muted-foreground'}`}
              />
              {statsLoading ? (
                <Skeleton className="h-4 w-12" />
              ) : (
                <span
                  className={`font-semibold tabular-nums ${!stats?.studiedToday && (stats?.streak ?? 0) > 0 ? 'text-muted-foreground' : ''}`}
                >
                  <AnimatedCounter value={stats?.streak ?? 0} /> day streak
                </span>
              )}
            </div>
            <div className="bg-muted/45 text-muted-foreground inline-flex h-8 items-center rounded-full px-3">
              {statsLoading ? (
                <Skeleton className="inline-block h-4 w-8" />
              ) : (
                <span className="font-medium tabular-nums">
                  {stats?.wordsReviewedThisWeek ?? 0}
                </span>
              )}{' '}
              reviewed this week
            </div>
            {(stats?.cardsDueToday ?? 0) > 0 && (
              <Link href="/collections?id=due_today">
                <Button
                  size="sm"
                  variant="default"
                  className="h-8 gap-1 rounded-full"
                >
                  <span className="font-semibold tabular-nums">
                    {stats?.cardsDueToday}
                  </span>{' '}
                  due
                  <IconArrowRight className="h-3 w-3" />
                </Button>
              </Link>
            )}
            {(insights?.averageAccuracy ?? 0) > 0 && (
              <div className="bg-muted/45 text-muted-foreground inline-flex h-8 items-center gap-1 rounded-full px-3">
                <span className="font-medium text-green-600 tabular-nums dark:text-green-400">
                  {insights?.averageAccuracy}%
                </span>{' '}
                accuracy
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconHelpCircle className="text-muted-foreground/60 h-3.5 w-3.5 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[200px] text-center"
                  >
                    <p>Percentage of reviews rated Good or Easy</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
            {(insights?.masteredCards ?? 0) > 0 && (
              <div className="bg-muted/45 text-muted-foreground hidden h-8 items-center rounded-full px-3 sm:inline-flex">
                <span className="font-medium tabular-nums">
                  {insights?.masteredCards}
                </span>{' '}
                mastered
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <>
            {/* Loading Skeleton */}
            <div className="space-y-4">
              <div>
                <Skeleton className="mb-2 h-6 w-32" />
                <Skeleton className="h-4 w-64" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardHeader>
                      <div className="mb-2 flex items-center gap-2">
                        <Skeleton className="h-5 w-5 rounded" />
                        <Skeleton className="h-5 w-32" />
                      </div>
                      <Skeleton className="h-4 w-24" />
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>
          </>
        ) : showAuthPrompt ? (
          <>
            <section className="bg-muted/35 flex flex-col gap-3 rounded-xl p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold">
                    Save and review cards
                  </h2>
                  <BodyText className="text-muted-foreground text-sm">
                    Sign in when you want synced decks and flashcards.
                  </BodyText>
                </div>
              </div>
              <LoginDialog>
                <Button className="h-10 w-full sm:w-auto">
                  {tCommon('signIn')}
                </Button>
              </LoginDialog>
            </section>
            {/* Show curated collections even when not logged in */}
            {curatedCollections.length > 0 && (
              <div className="space-y-4">
                <div>
                  <MinorHeading>Study paths</MinorHeading>
                  <BodyText className="text-muted-foreground text-sm">
                    Curated vocabulary lists you can study as decks.
                  </BodyText>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {curatedCollections.map((collection) => (
                    <CollectionTileLink
                      key={collection.id}
                      href={`/collections?id=${collection.id}`}
                      title={collection.name}
                      description={t('entryCount', {
                        count: collection.item_count
                      })}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Personal - Virtual Collections and Bookmarks */}
            {user && (virtualCollections.length > 0 || bookmarkToShow) && (
              <div
                className={`grid gap-3 sm:grid-cols-2 lg:grid-cols-3 ${collectionsFetching ? 'opacity-75' : ''}`}
              >
                {/* Bookmarks Collection - Always show for logged-in users */}
                {bookmarkToShow && (
                  <CollectionTileLink
                    href={`/collections?id=${bookmarkToShow.id}`}
                    icon={<IconBookmark className="h-4 w-4" />}
                    title={bookmarkToShow.name}
                    description={
                      bookmarkToShow.description ||
                      t('entryCount', {
                        count: existingBookmarkCollection?.item_count ?? 0
                      })
                    }
                    progress={userProgressMap?.get(bookmarkToShow.id)}
                    totalOverride={existingBookmarkCollection?.item_count}
                  />
                )}

                {/* Virtual Collections */}
                {virtualCollections.map((collection) => {
                  const Icon =
                    collection.virtualListType === 'review'
                      ? IconRefresh
                      : collection.virtualListType === 'due_today'
                        ? IconFlame
                        : IconCards
                  return (
                    <CollectionTileLink
                      key={collection.id}
                      href={`/collections?id=${collection.id}`}
                      icon={<Icon className="h-4 w-4" />}
                      title={collection.name}
                      description={
                        collection.description ||
                        t('entryCount', { count: collection.item_count })
                      }
                    />
                  )
                })}
              </div>
            )}

            {/* My Collections - Other user-created collections */}
            {user && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <MinorHeading>My Decks</MinorHeading>
                    <BodyText className="text-muted-foreground text-sm">
                      Your personal word, sentence, and custom-card decks.
                    </BodyText>
                  </div>
                  <CreateCollectionDialog
                    trigger={
                      <button
                        className="hover:bg-muted flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
                        aria-label={t('createCollection')}
                      >
                        <IconFolderPlus className="h-4 w-4" />
                        <span className="hidden sm:inline">Create deck</span>
                      </button>
                    }
                  />
                </div>
                {otherUserCollections.length > 0 ? (
                  <div
                    className={`grid gap-3 sm:grid-cols-2 lg:grid-cols-3 ${collectionsFetching ? 'opacity-75' : ''}`}
                  >
                    {otherUserCollections.map((collection) => {
                      const progress = userProgressMap?.get(collection.id)
                      return (
                        <CollectionTileLink
                          key={collection.id}
                          href={`/collections?id=${collection.id}`}
                          icon={<IconList className="h-4 w-4" />}
                          title={collection.name}
                          description={
                            collection.description ||
                            t('entryCount', { count: collection.item_count })
                          }
                          progress={progress}
                          totalOverride={collection.item_count}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
                    No decks yet. Create one or save a word to get started.
                  </div>
                )}
              </div>
            )}

            {/* Curated Collections - Always shown */}
            {curatedCollections.length > 0 && (
              <CuratedCollectionsSection
                collections={curatedCollections}
                groupedCurated={groupedCurated}
                curatedProgressMap={curatedProgressMap}
                userId={user?.id}
              />
            )}
          </>
        )}
      </div>
    </PageLayout>
  )
}

function CollectionTileLink({
  href,
  icon,
  title,
  description,
  progress,
  totalOverride
}: {
  href: string
  icon?: ReactNode
  title: string
  description?: string | null
  progress?: CollectionProgress
  totalOverride?: number
}) {
  return (
    <Link
      href={href}
      className="border-border/70 bg-background/78 hover:border-primary/35 hover:bg-muted/25 focus-visible:ring-ring/50 group flex min-h-[96px] min-w-0 flex-col justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-200 focus-visible:ring-[3px] focus-visible:outline-none"
    >
      <span className="flex min-w-0 items-start justify-between gap-3">
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-2">
            {icon && (
              <span className="bg-muted text-muted-foreground group-hover:text-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors">
                {icon}
              </span>
            )}
            <span className="min-w-0 truncate text-sm font-semibold">
              {title}
            </span>
          </span>
          {description && (
            <BodyText className="text-muted-foreground mt-1 line-clamp-2 text-xs">
              {description}
            </BodyText>
          )}
        </span>
        <IconArrowRight className="text-muted-foreground group-hover:text-foreground mt-1 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
      </span>
      {progress && (
        <CollectionProgressBar
          progress={progress}
          totalOverride={totalOverride}
          className="mt-auto"
        />
      )}
    </Link>
  )
}

// Category selector for curated collections.
function CuratedCollectionsSection({
  collections,
  groupedCurated,
  curatedProgressMap,
  userId
}: {
  collections: (CollectionDisplay & { coverIcon?: string | null })[]
  groupedCurated: Record<
    string,
    (CollectionDisplay & { coverIcon?: string | null })[]
  >
  curatedProgressMap?: Map<string, CollectionProgress>
  userId?: string
}) {
  const t = useTranslations('collections')
  const categories = useMemo(
    () => ['All', ...Object.keys(groupedCurated)],
    [groupedCurated]
  )
  const [selectedCategory, setSelectedCategory] = useState('All')

  const filteredCollections = useMemo(() => {
    if (selectedCategory === 'All') return collections
    return groupedCurated[selectedCategory] || []
  }, [selectedCategory, collections, groupedCurated])

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <MinorHeading>Study paths</MinorHeading>
          <BodyText className="text-muted-foreground text-sm">
            Curated vocabulary lists for structured review.
          </BodyText>
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="bg-background/80 h-10 w-full sm:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            {categories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {filteredCollections.map((collection) => {
          const progress = curatedProgressMap?.get(collection.slug ?? '')
          return (
            <CollectionTileLink
              key={collection.id}
              href={`/collections?id=${collection.id}`}
              title={collection.name}
              description={t('entryCount', { count: collection.item_count })}
              progress={userId ? progress : undefined}
              totalOverride={collection.item_count}
            />
          )
        })}
      </div>
    </div>
  )
}

function CollectionDetailView({
  collectionId,
  onBack
}: {
  collectionId: CollectionId
  onBack: () => void
}) {
  const router = useRouter()
  const { user, isLoading: authLoading } = useAuth()
  const { isOfflineEnabled: isOfflineMode } = useOffline()
  const t = useTranslations('collections')
  const tCommon = useTranslations('common')
  const locale = useLocale()
  const [studyMode, setStudyMode] = useState(false)
  const [viewMode, setViewMode] = useState<'list' | 'table'>('table')
  const [activeTab, setActiveTab] = useState('cards')
  const [itemToRemove, setItemToRemove] = useState<StudyDeckItem | null>(null)
  const [itemToEdit, setItemToEdit] = useState<StudyDeckItem | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [deckEditOpen, setDeckEditOpen] = useState(false)
  const [deckNameDraft, setDeckNameDraft] = useState('')
  const [deckDescriptionDraft, setDeckDescriptionDraft] = useState('')
  const [settingsNewCards, setSettingsNewCards] = useState('')
  const [settingsReviews, setSettingsReviews] = useState('')
  const [settingsRolloverHour, setSettingsRolloverHour] = useState('')

  const collectionType = getCollectionType(collectionId)
  const curatedSlug = getCuratedSlug(collectionId)

  // Get collection metadata
  const {
    data: collection,
    isLoading: collectionLoading,
    isFetched: collectionFetched
  } = useQuery({
    queryKey: ['collection', collectionId, user?.id],
    queryFn: () => {
      if (!user?.id && collectionType !== 'curated') return null
      return getCollectionById(collectionId, user?.id ?? null)
    },
    enabled: !authLoading && (!!user?.id || collectionType === 'curated'),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  })

  const collectionName = collection?.name || 'Collection'
  const collectionDescription = collection?.description || undefined
  const totalCount = collection?.item_count || 0
  const isPersonalDeck = collectionType === 'user' && !!collection
  const canManageDeck = isPersonalDeck && !collection?.is_default

  // Fetch user settings for curated collections
  const { data: userSettings } = useQuery({
    queryKey: ['user-collection-settings', collectionId, user?.id],
    queryFn: async () => {
      if (!user?.id || collectionType !== 'curated') return null
      const slug = getCuratedSlug(collectionId)
      if (!slug) return null
      return getUserCollectionSettings(user.id, slug)
    },
    enabled: !!user?.id && collectionType === 'curated',
    staleTime: 1000 * 60 * 5
  })

  // Fetch mixed word/sentence cards with infinite query.
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: entriesLoading,
    isFetching: entriesFetching
  } = useInfiniteQuery({
    queryKey: ['study-deck-items', collectionId, user?.id, isOfflineMode],
    queryFn: async ({ pageParam = 0 }) => {
      return getStudyDeckItems(collectionId, user?.id ?? '', pageParam, 50)
    },
    enabled: !authLoading && (!!user?.id || collectionType === 'curated'),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.length === 50 ? allPages.length : undefined
    },
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  })

  const items: StudyDeckItem[] = data?.pages.flatMap((page) => page) || []

  const queryClient = useQueryClient()

  // Remove card mutation
  const removeEntryMutation = useMutation({
    mutationFn: async (item: StudyDeckItem) => {
      if (!user?.id) throw new Error('User not logged in')
      if (collection?.is_bookmark && item.dict_entry_id) {
        await removeBookmark(user.id, item.dict_entry_id)
        return
      }
      await removeCardFromDeck(collectionId, item.card_id)
    },
    onSuccess: () => {
      toast.success(
        collection?.is_bookmark ? 'Removed from bookmarks' : 'Removed from deck'
      )
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: ['study-deck-items', collectionId, user?.id]
      })
      queryClient.invalidateQueries({
        queryKey: ['collection', collectionId, user?.id]
      })
      queryClient.invalidateQueries({
        queryKey: ['all-collections', user?.id]
      })
      if (collection?.is_bookmark) {
        queryClient.invalidateQueries({
          queryKey: ['bookmark', undefined, user?.id],
          exact: false
        })
      }
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to remove card from deck'
      )
    }
  })

  const updateDeckMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !isPersonalDeck) throw new Error('Deck not found')
      return updateStudyDeck(user.id, collectionId, {
        name: deckNameDraft.trim(),
        description: deckDescriptionDraft.trim() || undefined
      })
    },
    onSuccess: () => {
      toast.success('Deck updated')
      setDeckEditOpen(false)
      queryClient.invalidateQueries({
        queryKey: ['collection', collectionId, user?.id]
      })
      queryClient.invalidateQueries({ queryKey: ['all-collections', user?.id] })
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Could not update deck'
      )
    }
  })

  const duplicateDeckMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !isPersonalDeck) throw new Error('Deck not found')
      return duplicateStudyDeck(user.id, collectionId)
    },
    onSuccess: (deck) => {
      toast.success('Deck duplicated')
      queryClient.invalidateQueries({ queryKey: ['all-collections', user?.id] })
      router.push(`/collections?id=${deck.id}`)
    }
  })

  const deleteDeckMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !canManageDeck)
        throw new Error('This deck cannot be deleted')
      await deleteStudyDeck(user.id, collectionId)
    },
    onSuccess: () => {
      toast.success('Deck deleted')
      queryClient.invalidateQueries({ queryKey: ['all-collections', user?.id] })
      router.push('/collections')
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Could not delete deck'
      )
    }
  })

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async (settings: {
      newCardsLimit: number | null
      reviewLimit: number | null
      rolloverHour: number | null
    }) => {
      if (!user?.id || collectionType !== 'curated') {
        throw new Error(
          'Can only save settings for curated collections when logged in'
        )
      }
      const slug = getCuratedSlug(collectionId)
      if (!slug) throw new Error('Invalid curated collection')

      return upsertUserCollectionSettings(user.id, slug, {
        new_cards_limit: settings.newCardsLimit,
        review_limit: settings.reviewLimit,
        rollover_hour: settings.rolloverHour
      })
    },
    onSuccess: () => {
      toast.success('Settings saved successfully')
      setSettingsOpen(false)
      // Invalidate the settings query to refetch
      queryClient.invalidateQueries({
        queryKey: ['user-collection-settings', collectionId, user?.id]
      })
      // Invalidate progress queries so collection cards update with new limits
      if (user?.id) {
        invalidateCardQueries(queryClient, user.id)
      }
    },
    onError: (error) => {
      toast.error(`Failed to save settings: ${error.message}`)
    }
  })

  const handleRemoveEntry = useCallback((item: StudyDeckItem) => {
    setItemToRemove(item)
    setConfirmDialogOpen(true)
  }, [])

  const confirmRemoveEntry = useCallback(() => {
    if (itemToRemove) {
      removeEntryMutation.mutate(itemToRemove)
      setConfirmDialogOpen(false)
      setItemToRemove(null)
    }
  }, [itemToRemove, removeEntryMutation])

  const handleOpenSettings = useCallback(() => {
    // Pre-populate with current settings
    setSettingsNewCards(userSettings?.new_cards_limit?.toString() || '')
    setSettingsReviews(userSettings?.review_limit?.toString() || '')
    setSettingsRolloverHour(userSettings?.rollover_hour?.toString() || '')
    setSettingsOpen(true)
  }, [userSettings])

  const handleOpenDeckEdit = useCallback(() => {
    setDeckNameDraft(collectionName)
    setDeckDescriptionDraft(collectionDescription || '')
    setDeckEditOpen(true)
  }, [collectionName, collectionDescription])

  const handleSaveSettings = useCallback(() => {
    const newCardsLimit = settingsNewCards ? parseInt(settingsNewCards) : null
    const reviewLimit = settingsReviews ? parseInt(settingsReviews) : null
    const rolloverHour = settingsRolloverHour
      ? parseInt(settingsRolloverHour)
      : null

    // Basic validation
    if (newCardsLimit !== null && (newCardsLimit < 1 || newCardsLimit > 200)) {
      toast.error('New cards per day must be between 1 and 200')
      return
    }
    if (reviewLimit !== null && (reviewLimit < 10 || reviewLimit > 1000)) {
      toast.error('Reviews per day must be between 10 and 1000')
      return
    }
    if (rolloverHour !== null && (rolloverHour < 0 || rolloverHour > 23)) {
      toast.error('Rollover hour must be between 0 and 23')
      return
    }

    saveSettingsMutation.mutate({
      newCardsLimit,
      reviewLimit,
      rolloverHour
    })
  }, [
    settingsNewCards,
    settingsReviews,
    settingsRolloverHour,
    saveSettingsMutation
  ])

  const cancelRemove = useCallback(() => {
    setConfirmDialogOpen(false)
    setItemToRemove(null)
  }, [])

  const handleStartStudy = useCallback(() => {
    setStudyMode(true)
  }, [])

  const handleExitStudy = useCallback(() => {
    setStudyMode(false)
  }, [])

  // Determine if collection can be studied (has cards or entries)
  const canStudy =
    user &&
    (collectionType === 'user' ||
      collectionType === 'curated' ||
      (collectionType === 'virtual' && totalCount > 0))

  // Study session props
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC'

  if (studyMode && user && canStudy) {
    return (
      <PageLayout
        variant="workspace"
        title={collectionName}
        description={collectionDescription || ''}
      >
        <div className="mx-auto box-border flex w-full max-w-5xl flex-1 flex-col gap-4 px-4 py-5 md:px-6 md:py-6">
          <Button
            variant="ghost"
            size="sm"
            className="w-fit gap-2"
            onClick={handleExitStudy}
          >
            <IconArrowLeft className="h-4 w-4" />
            {t('backToCollection')}
          </Button>
          <FlashcardSession
            collectionId={collectionId}
            curatedSlug={curatedSlug}
            userId={user.id}
            timeZone={timeZone}
            onExit={handleExitStudy}
          />
        </div>
      </PageLayout>
    )
  }

  // Determine render state
  const showSkeletons =
    authLoading ||
    (entriesLoading &&
      items.length === 0 &&
      (user || collectionType === 'curated' || collectionType === 'virtual')) ||
    (collectionType === 'curated' && !collection && items.length === 0)
  const showAuthPrompt =
    !authLoading &&
    !user &&
    collectionType !== 'curated' &&
    collectionType !== 'virtual'
  const showNotFound =
    !authLoading &&
    !collectionLoading &&
    collectionFetched &&
    collection === null && // Explicitly null (not undefined, not loading)
    (collectionType === 'curated' || (collectionType === 'user' && user))
  const showEmpty =
    !authLoading &&
    !entriesLoading &&
    !showNotFound &&
    (collection || collectionType === 'virtual') && // Virtual collections may not have collection object
    items.length === 0 &&
    (user || collectionType === 'curated' || collectionType === 'virtual')

  return (
    <PageLayout
      variant="workspace"
      title={collectionName}
      description={collectionDescription || ''}
    >
      <div className="mx-auto box-border flex w-full max-w-6xl flex-1 flex-col gap-4 px-4 py-5 md:px-6 md:py-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            className="w-fit gap-2"
            onClick={onBack}
          >
            <IconArrowLeft className="h-4 w-4" />
            Back to decks
          </Button>
          <div className="flex items-center gap-2">
            {isPersonalDeck && (
              <DeckActions
                mode="menu"
                canDelete={canManageDeck}
                duplicatePending={duplicateDeckMutation.isPending}
                deletePending={deleteDeckMutation.isPending}
                onEdit={handleOpenDeckEdit}
                onDuplicate={() => duplicateDeckMutation.mutate()}
                onDelete={() => deleteDeckMutation.mutate()}
              />
            )}
            {/* Settings button for curated collections */}
            {collectionType === 'curated' && user?.id && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleOpenSettings}
                className="gap-2"
              >
                <IconSettings className="h-4 w-4" />
                <span className="hidden sm:inline">{t('settings')}</span>
              </Button>
            )}

            {activeTab === 'cards' && (
              <div className="border-border flex items-center rounded-md border">
                <Button
                  variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="rounded-r-none border-r"
                  onClick={() => setViewMode('table')}
                >
                  <IconTable className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="rounded-l-none"
                  onClick={() => setViewMode('list')}
                >
                  <IconList className="h-4 w-4" />
                </Button>
              </div>
            )}
            {canStudy && (
              <Button size="sm" onClick={handleStartStudy}>
                <IconPlayerPlay className="h-4 w-4" />
                <span className="ml-1">{t('study')}</span>
              </Button>
            )}
          </div>
        </div>

        <div className="border-border/70 bg-background/70 flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
            <span>{t('entryCount', { count: totalCount })}</span>
            {collection?.is_default && (
              <span className="bg-muted text-muted-foreground rounded-full px-2 py-1 text-xs font-medium">
                Default deck
              </span>
            )}
          </div>
          {isPersonalDeck && <AddStudyNoteButton deckId={collectionId} />}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="gap-4">
          <TabsList>
            <TabsTrigger value="cards">Cards</TabsTrigger>
            {isPersonalDeck && (
              <TabsTrigger value="import">Import / Export</TabsTrigger>
            )}
            {isPersonalDeck && (
              <TabsTrigger value="settings">Settings</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="cards" className="space-y-4">
            {showSkeletons ? (
              <div className="space-y-0 divide-y rounded-lg border">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="p-4">
                    <div className="flex flex-col gap-2">
                      <Skeleton className="h-7 w-32" />
                      <Skeleton className="h-4 w-full max-w-md" />
                      <Skeleton className="h-4 w-2/3 max-w-sm" />
                    </div>
                  </div>
                ))}
              </div>
            ) : showAuthPrompt ? (
              <SignInPrompt
                title="Sign in to view this deck"
                description={t('signInDescription')}
                icon={
                  <IconBookmark className="text-muted-foreground h-8 w-8" />
                }
                buttonText={tCommon('signIn')}
              />
            ) : showNotFound ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <BodyText className="text-muted-foreground font-medium">
                    Deck not found
                  </BodyText>
                  <BodyText className="text-muted-foreground mt-2 text-sm">
                    This deck may have been deleted or moved.
                  </BodyText>
                </CardContent>
              </Card>
            ) : showEmpty ? (
              <Card>
                <CardContent className="py-10 text-center">
                  <BodyText className="font-medium">No cards yet</BodyText>
                  <BodyText className="text-muted-foreground mt-2 text-sm">
                    Add a card manually, mine a sentence from the reader, or
                    import a CSV.
                  </BodyText>
                  {isPersonalDeck && (
                    <div className="mt-4 flex justify-center">
                      <AddStudyNoteButton deckId={collectionId} />
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div
                className={
                  entriesFetching ? 'opacity-75 transition-opacity' : ''
                }
              >
                {viewMode === 'table' ? (
                  <CollectionItemsTable
                    items={items}
                    locale={locale}
                    onEdit={isPersonalDeck ? setItemToEdit : undefined}
                    onRemove={
                      user &&
                      (collectionType === 'user' || collection?.is_bookmark)
                        ? handleRemoveEntry
                        : undefined
                    }
                    isRemoving={removeEntryMutation.isPending}
                  />
                ) : (
                  <div className="grid gap-3">
                    {items.map((item) => (
                      <div
                        key={item.card_id}
                        className="bg-card rounded-lg border p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="line-clamp-2 text-base font-semibold">
                              {item.front}
                            </p>
                            {item.back && (
                              <p className="text-muted-foreground mt-1 line-clamp-2 text-sm whitespace-pre-line">
                                {item.back}
                              </p>
                            )}
                          </div>
                          {isPersonalDeck && (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => setItemToEdit(item)}
                                aria-label="Edit card"
                              >
                                <IconEdit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => handleRemoveEntry(item)}
                                disabled={removeEntryMutation.isPending}
                                aria-label="Remove from deck"
                              >
                                <IconX className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {hasNextPage && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      variant="outline"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                    >
                      {isFetchingNextPage ? (
                        <>
                          <IconRefresh className="mr-2 h-4 w-4 animate-spin" />
                          {tCommon('loading')}
                        </>
                      ) : (
                        t('loadMoreEntries')
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {isPersonalDeck && (
            <TabsContent value="import">
              <DeckImportExportPanel
                deckId={collectionId}
                deckName={collectionName}
              />
            </TabsContent>
          )}

          {isPersonalDeck && (
            <TabsContent value="settings">
              <div className="max-w-2xl rounded-lg border p-4">
                <h3 className="text-sm font-semibold">Deck settings</h3>
                <BodyText className="text-muted-foreground mt-1 text-sm">
                  Edit the deck label shown across Miaozi. Review scheduling
                  stays automatic.
                </BodyText>
                <DeckActions
                  canDelete={canManageDeck}
                  duplicatePending={duplicateDeckMutation.isPending}
                  deletePending={deleteDeckMutation.isPending}
                  onEdit={handleOpenDeckEdit}
                  onDuplicate={() => duplicateDeckMutation.mutate()}
                  onDelete={() => deleteDeckMutation.mutate()}
                />
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {collection?.is_bookmark
                ? 'Remove from Saved Words?'
                : 'Remove from Deck?'}
            </DialogTitle>
            <DialogDescription>
              {itemToRemove && (
                <>
                  Are you sure you want to remove{' '}
                  <strong>{itemToRemove.front}</strong> from{' '}
                  {collection?.is_bookmark ? 'Saved Words' : 'this deck'}? The
                  card can still remain in other decks.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelRemove}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemoveEntry}
              disabled={removeEntryMutation.isPending}
            >
              {removeEntryMutation.isPending ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StudyNoteDialog
        deckId={collectionId}
        item={itemToEdit}
        open={!!itemToEdit}
        onOpenChange={(open) => {
          if (!open) setItemToEdit(null)
        }}
      />

      <Dialog open={deckEditOpen} onOpenChange={setDeckEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit deck</DialogTitle>
            <DialogDescription>
              Rename this deck or adjust the short description shown on the deck
              list.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="deck-name">Name</Label>
              <Input
                id="deck-name"
                value={deckNameDraft}
                onChange={(event) => setDeckNameDraft(event.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="deck-description">Description</Label>
              <Input
                id="deck-description"
                value={deckDescriptionDraft}
                onChange={(event) =>
                  setDeckDescriptionDraft(event.target.value)
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeckEditOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!deckNameDraft.trim() || updateDeckMutation.isPending}
              onClick={() => updateDeckMutation.mutate()}
            >
              {updateDeckMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deck Settings</DialogTitle>
            <DialogDescription>
              Customize your study settings for this deck. Leave fields empty to
              use default values.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-cards">New Cards Per Day</Label>
              <Input
                id="new-cards"
                type="number"
                placeholder="20 (default)"
                value={settingsNewCards}
                onChange={(e) => setSettingsNewCards(e.target.value)}
                min={1}
                max={200}
              />
              <p className="text-muted-foreground text-sm">
                Number of new cards to introduce daily (1-200)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reviews">Reviews Per Day</Label>
              <Input
                id="reviews"
                type="number"
                placeholder="200 (default)"
                value={settingsReviews}
                onChange={(e) => setSettingsReviews(e.target.value)}
                min={10}
                max={1000}
              />
              <p className="text-muted-foreground text-sm">
                Maximum number of review cards per day (10-1000)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rollover-hour">Day Rollover Hour</Label>
              <Input
                id="rollover-hour"
                type="number"
                placeholder="4 (default)"
                value={settingsRolloverHour}
                onChange={(e) => setSettingsRolloverHour(e.target.value)}
                min={0}
                max={23}
              />
              <p className="text-muted-foreground text-sm">
                Hour when your study day resets (0-23, e.g., 4 = 4:00 AM)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveSettings}
              disabled={saveSettingsMutation.isPending}
            >
              {saveSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  )
}

function CollectionsFallback() {
  return (
    <PageLayout
      variant="workspace"
      title="Decks"
      description="Manage cards and study with spaced repetition."
    >
      <div className="mx-auto box-border flex w-full max-w-5xl flex-1 flex-col gap-6 px-4 py-5 md:px-6 md:py-6">
        <div className="space-y-4">
          <div>
            <Skeleton className="mb-2 h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <div className="mb-2 flex items-center gap-2">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                  <Skeleton className="h-4 w-24" />
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export function AppCollectionsClient() {
  return (
    <Suspense fallback={<CollectionsFallback />}>
      <AppCollectionsContent />
    </Suspense>
  )
}
